// ProdutosDestaque.js — Seção "Produtos em Destaque" como GRID (não carrossel).
//
// QUANTOS livros aparecem é decisão do SERVIDOR (`insigne.destaque_vitrine_total`,
// hoje 5), e não deste arquivo. É o mesmo número que decide quem tem o desconto
// dos destaques: todo livro desta seção está em promoção, e quem sai dela perde.
// Ver App\Services\VitrineDestaqueService na API.
import React, { useEffect, useState } from "react";
import CardDestaque from "./CardDestaque";
import useHomeConfig from "../../../hooks/useHomeConfig";
import Vitrine from "../vitrine/Vitrine";
import RevealStagger from "../../ui/RevealStagger";
import CardSkeleton from "../../ui/CardSkeleton";

const ProdutosDestaque = ({
  nome = "Produtos em Destaque",
  icone = "bi-patch-exclamation-fill",
  // O chapéu da moldura. Padrão vazio: quem reaproveitar a seção para outra
  // lista escreve o seu, em vez de herdar em silêncio um contexto errado.
  chapeu = "Curadoria da casa",
  endpoint = "ebook",
  limit = 5,
  // Destino do "Ver todos". É prop porque a seção é genérica (recebe nome e
  // endpoint): quem reaproveitá-la para outra lista troca o destino junto, em
  // vez de herdar em silêncio um link que manda para o catálogo errado.
  verTodosPara,
}) => {
  const [livros, setLivros] = useState([]);
  // A lista veio da curadoria do servidor (e não do fallback do catálogo)? Só
  // então os cards são "Destaque" de verdade — ver a fita, mais abaixo.
  const [daVitrine, setDaVitrine] = useState(false);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_API;
  const main = process.env.REACT_APP_MAIN;
  const config = useHomeConfig();

  useEffect(() => {
    // Seção de destaques (endpoint padrão): usa a vitrine montada no servidor
    // (fixos do painel, completados por recentes/mais_vendido/maior_preco).
    //
    // SEM `slice`. A API já manda exatamente os livros da vitrine, e são eles
    // que têm o desconto. Cortar aqui de novo foi o que criou três números
    // diferentes (20 buscados, 12 enviados, 5 desenhados); se a config subir
    // para 10, a home tem de desenhar 10, ou cinco livros teriam desconto sem
    // aparecer em destaque.
    //
    // `vivo` descarta resposta de uma rodada anterior. Numa primeira visita a
    // config ainda não chegou, então este efeito começa baixando o fallback
    // (`/ebook`, o catálogo inteiro). A config chega antes dele, a vitrine é
    // desenhada — e o catálogo, chegando por último, SOBRESCREVIA a vitrine com
    // os cinco primeiros do acervo. Agora a rodada velha só grava se ainda for
    // a rodada atual.
    let vivo = true;

    if (endpoint === "ebook" && config?.destaque?.livros?.length) {
      setLivros(config.destaque.livros);
      setDaVitrine(true);
      setLoading(false);
      return () => { vivo = false; };
    }

    const fetchLivros = async () => {
      try {
        const r = await fetch(API_URL + endpoint);
        if (r.ok) {
          const dados = (await r.json()).slice(0, limit);
          if (vivo) {
            setLivros(dados);
            setDaVitrine(false);
          }
        }
      } catch (e) {
        console.error("Erro ao carregar destaques:", e);
      } finally {
        if (vivo) setLoading(false);
      }
    };
    fetchLivros();
    return () => { vivo = false; };
  }, [API_URL, endpoint, limit, config]);

  // Vitrine vazia não é vitrine: sem livros, a moldura ficaria anunciando uma
  // seção que não tem conteúdo nenhum embaixo. Some inteira.
  if (!loading && livros.length === 0) return null;

  return (
    <Vitrine
      chapeu={chapeu}
      titulo={nome}
      icone={icone}
      // A FRASE MUDA COM A ORIGEM DA LISTA, e isso não é detalhe de texto: a
      // promoção acompanha a vitrine do servidor. No fallback (o catálogo
      // inteiro, quando a config não carregou) estes cinco livros não são
      // destaque de ninguém e não têm desconto — prometer os dois ali seria a
      // mesma mentira que a fita "Destaque" evita não sendo desenhada.
      descricao={
        daVitrine
          ? "Escolhidos a dedo pela editora. Enquanto estiverem aqui, saem com desconto."
          : "Uma amostra do que a editora publica."
      }
      verTodos={{ to: verTodosPara || main + "livros", texto: "Ver o acervo" }}
    >
      {loading ? (
        // Esqueleto no lugar do spinner: ocupa o mesmo espaço dos cards, então
        // a chegada dos livros não empurra o resto da home para baixo.
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-3 md:gap-x-4 gap-y-12 pb-10 orfa-ao-centro">
          {Array.from({ length: limit }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : (
        // Grid responsivo: 2 -> 3 -> 4 -> 5 colunas conforme o tamanho da tela
        <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-3 md:gap-x-4 gap-y-12 pb-10 orfa-ao-centro">
          {livros.map((livro) => (
            <RevealStagger.Item key={livro.id} className="h-full">
              {/* ===== A FITA, O DESCONTO E A VITRINE SÃO O MESMO CONJUNTO =====
                  Em 10/09/2026 o cliente viu quatro cards com fita e "10% OFF"
                  e o quinto sem nada, a preço cheio: o desconto seguia só os
                  fixos do painel, e o quinto vinha do preenchimento automático.
                  Ele decidiu que TODO livro da vitrine tem a promoção, e quem
                  sai da vitrine perde.

                  Por isso a fita vai em todo card desta seção — e só quando a
                  lista veio da vitrine do servidor. No fallback (`/ebook`,
                  quando a config não carregou) os cinco primeiros do catálogo
                  não são destaque de ninguém, e a fita mentiria. */}
              <CardDestaque livro={livro} selo={daVitrine ? "Destaque" : null} />
            </RevealStagger.Item>
          ))}
        </RevealStagger>
      )}
    </Vitrine>
  );
};

export default ProdutosDestaque;
